import React, { useState } from "react";

const TIME_SLOTS = ["0600", "1200", "1800", "2400"];

const COLUMNS = [
  { key: "windSpeed", label: "Speed", group: "Wind" },
  { key: "windDir", label: "Dir", group: "Wind" },
  { key: "windHeight", label: "Height", group: "Wind" },
  { key: "seaPeriod", label: "Period", group: "Sea" },
  { key: "seaHeight", label: "Height", group: "Sea" },
  { key: "swellDir", label: "Dir", group: "Swell" },
  { key: "swellPeriod", label: "Period", group: "Swell" },
  { key: "pitch", label: "Pitch", group: "Motion" },
  { key: "roll", label: "Roll", group: "Motion" },
];

const emptyRow = (dprId, timeSlot) => ({
  dprId,
  timeSlot,
  windSpeed: "",
  windDir: "",
  windHeight: "",
  seaPeriod: "",
  seaHeight: "",
  swellDir: "",
  swellPeriod: "",
  pitch: "",
  roll: "",
});

const WeatherTable = ({ weather, dprId, onSave }) => {
  const getRow = (slot) =>
    weather.find((w) => w.timeSlot === slot) || emptyRow(dprId, slot);

  const [rows, setRows] = useState(() =>
    TIME_SLOTS.map((slot) => getRow(slot))
  );
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const handleStartEdit = (slotIdx, field, value) => {
    setEditingCell({ slotIdx, field });
    setDraftValue(value ?? "");
  };

  const handleCommit = () => {
    if (!editingCell) return;
    const { slotIdx, field } = editingCell;
    const updated = rows.map((row, i) =>
      i === slotIdx ? { ...row, [field]: draftValue } : row
    );
    setRows(updated);
    setEditingCell(null);
    setDraftValue("");
    onSave(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCommit();
    if (e.key === "Escape") {
      setEditingCell(null);
      setDraftValue("");
    }
  };

  const renderCell = (row, slotIdx, field) => {
    const isEditing =
      editingCell?.slotIdx === slotIdx && editingCell?.field === field;

    if (isEditing) {
      return (
        <input
          type="text"
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          className="w-full rounded border border-navy-600 bg-white px-1 py-0.5 text-center text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-navy-600/20"
        />
      );
    }

    return (
      <div
        onClick={() => handleStartEdit(slotIdx, field, row[field] || "")}
        className="cursor-pointer min-h-[1.5em] rounded text-center font-mono tabular-nums hover:bg-navy-50/60"
      >
        {row[field] || <span className="text-zinc-300">—</span>}
      </div>
    );
  };

  return (
    <section>
      <h3 className="section-eyebrow mb-3">Weather</h3>
      <div className="overflow-x-auto">
        <table className="table-hairline">
          <thead>
            <tr>
              <th rowSpan={2} className="w-16 align-bottom">
                Time
              </th>
              <th colSpan={3} className="text-center text-zinc-700">
                Wind
              </th>
              <th colSpan={2} className="text-center text-zinc-700">
                Sea
              </th>
              <th colSpan={2} className="text-center text-zinc-700">
                Swell
              </th>
              <th colSpan={2} className="text-center text-zinc-700">
                Motion
              </th>
            </tr>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className="text-center font-normal normal-case tracking-normal text-[11px] text-zinc-500">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, slotIdx) => (
              <tr key={row.timeSlot}>
                <td className="num text-center font-medium text-zinc-700">
                  {row.timeSlot}
                </td>
                {COLUMNS.map((col) => (
                  <td key={col.key}>{renderCell(row, slotIdx, col.key)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default WeatherTable;
